<template>
  <div class="crm-options scroll-container p-3">
    <form>
      <section class="behavior-section mb-3">
        <div class="behavior-copy">
          <h6>Comportamento ao criar</h6>
          <p>
            Defina se sempre deseja criar um novo lead ou atualizar um lead
            existente quando encontrado.
          </p>
        </div>

        <div
          class="behavior-options"
          role="radiogroup"
          aria-label="Comportamento ao criar lead"
        >
          <label
            class="behavior-option"
            :class="{ selected: formData.always_create_new_lead }"
          >
            <input
              v-model="formData.always_create_new_lead"
              class="sr-only"
              type="radio"
              :value="true"
              @change="onLeadBehaviorChange"
            />
            <span class="radio-indicator" aria-hidden="true"></span>
            <span class="behavior-icon" aria-hidden="true">
              <i class="fas fa-user-plus"></i>
            </span>
            <span>
              <strong>Sempre criar novo lead</strong>
              <small>Um novo lead será criado mesmo que já exista.</small>
            </span>
          </label>

          <label
            class="behavior-option"
            :class="{ selected: !formData.always_create_new_lead }"
          >
            <input
              v-model="formData.always_create_new_lead"
              class="sr-only"
              type="radio"
              :value="false"
              @change="onLeadBehaviorChange"
            />
            <span class="radio-indicator" aria-hidden="true"></span>
            <span class="behavior-icon" aria-hidden="true">
              <i class="fas fa-user-check"></i>
            </span>
            <span>
              <strong>Atualizar lead existente (se houver)</strong>
              <small>
                Se um lead existir, ele será atualizado. Caso contrário, um novo
                será criado.
              </small>
            </span>
          </label>
        </div>
      </section>

      <section
        v-if="!formData.always_create_new_lead"
        class="stage-filter-section mb-4"
      >
        <div class="stage-filter-copy">
          <h6>Atualizar leads de quais estágios? <span>(opcional)</span></h6>
          <p>Selecione os estágios dos leads que poderão ser atualizados.</p>
          <small>Deixe vazio para atualizar leads de qualquer estágio.</small>
        </div>

        <b-dropdown
          class="stage-dropdown"
          menu-class="stage-dropdown-menu"
          variant="outline-primary"
          no-flip
          @hide="stageSearch = ''"
        >
          <template #button-content>
            <span>{{ selectedStagesLabel }}</span>
            <i class="fas fa-chevron-down"></i>
          </template>

          <b-dropdown-form @submit.stop.prevent>
            <div class="stage-search">
              <i class="fas fa-search" aria-hidden="true"></i>
              <b-form-input
                v-model.trim="stageSearch"
                placeholder="Buscar estágio..."
                autocomplete="off"
                @click.stop
              ></b-form-input>
            </div>

            <div class="stage-list">
              <label
                v-if="updatableStageOptions.length"
                class="stage-checkbox select-all"
              >
                <input
                  type="checkbox"
                  :checked="allStagesSelected"
                  :indeterminate.prop="someStagesSelected"
                  @change="toggleAllStages"
                />
                <span>Selecionar todos</span>
              </label>

              <label
                v-for="stage in filteredStageOptions"
                :key="stage.value"
                class="stage-checkbox"
              >
                <input
                  v-model="formData.updatable_lead_pipeline_stage_ids"
                  type="checkbox"
                  :value="stage.value"
                  @change="updateJson"
                />
                <span>{{ stage.text }}</span>
              </label>

              <p v-if="!formData.lead_pipeline_id" class="stage-empty">
                Selecione um funil para listar os estágios.
              </p>
              <p v-else-if="!filteredStageOptions.length" class="stage-empty">
                Nenhum estágio encontrado.
              </p>
            </div>
          </b-dropdown-form>
        </b-dropdown>
      </section>

      <b-row class="mb-3">
        <b-col cols="12" md="4">
          <b-form-group label="Título" label-for="title-input">
            <b-form-input
              type="text"
              v-model="formData.title"
              placeholder="Título do lead"
              @input="updateJson"
              @focus="handleFocus"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="8">
          <b-form-group label="Descrição" label-for="description-input">
            <b-form-input
              type="text"
              v-model="formData.description"
              placeholder="Descrição do lead"
              @input="updateJson"
              @focus="handleFocus"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Funil" label-for="pipeline-input">
            <b-form-select
              id="pipeline-input"
              v-model="formData.lead_pipeline_id"
              :options="funisOptions"
              @change="onFunilChange"
            >
              <template #first>
                <b-form-select-option :value="null"
                  >Selecione um funil</b-form-select-option
                >
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="4">
          <b-form-group label="Estágio" label-for="stage-input">
            <b-form-select
              id="stage-input"
              v-model="formData.lead_pipeline_stage_id"
              :options="estagiosOptions"
              @change="updateJson"
              :disabled="!formData.lead_pipeline_id"
            >
              <template #first>
                <b-form-select-option :value="null"
                  >Selecione um estágio</b-form-select-option
                >
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="4">
          <b-form-group label="Produto (opcional)" label-for="produto-input">
            <b-form-select
              id="produto-input"
              v-model="formData.product_id"
              :options="produtosOptions"
              @change="updateJson"
            >
              <template #first>
                <b-form-select-option :value="null"
                  >Nenhum produto</b-form-select-option
                >
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="4">
          <b-form-group label="Origem" label-for="origem-input">
            <b-form-select
              id="origem-input"
              v-model="formData.lead_source_id"
              :options="origensOptions"
              @change="updateJson"
            >
              <template #first>
                <b-form-select-option :value="null"
                  >Selecione uma origem</b-form-select-option
                >
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="4">
          <b-form-group label="Tipo de Lead" label-for="tipo-lead-input">
            <b-form-select
              id="tipo-lead-input"
              v-model="formData.lead_type_id"
              :options="tiposLeadOptions"
              @change="updateJson"
            >
              <template #first>
                <b-form-select-option :value="null"
                  >Selecione um tipo</b-form-select-option
                >
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="4">
          <b-form-group
            label="Responsável pela venda (opcional)"
            label-for="usuario-input"
          >
            <b-form-select
              id="usuario-input"
              v-model="formData.user_id"
              :options="usuariosOptions"
              @change="updateJson"
            >
              <template #first>
                <b-form-select-option :value="null"
                  >Nenhum responsável</b-form-select-option
                >
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
    </form>
  </div>
</template>

<script>
import ApiService from "../../core/services/api.service";

export default {
  name: "CrmOptions",
  components: {},
  data() {
    return {
      crmConfig: null,
      loading: true,
      parametrosCarregados: false,
      formData: {
        title: null,
        description: null,
        lead_pipeline_id: null,
        lead_pipeline_stage_id: null,
        lead_source_id: null,
        lead_type_id: null,
        user_id: null,
        product_id: null,
        always_create_new_lead: false,
        updatable_lead_pipeline_stage_ids: []
      },
      formatIsValidJson: true,
      headersIsValidJson: true,
      parsedJson: null,
      estagiosOptions: [],
      stageSearch: ""
    };
  },
  computed: {
    produtosOptions() {
      if (!this.crmConfig?.produtos) return [];
      return this.crmConfig.produtos.map((produto) => ({
        value: produto.id,
        text: produto.name
      }));
    },
    origensOptions() {
      if (!this.crmConfig?.origens) return [];
      return this.crmConfig.origens.map((origem) => ({
        value: origem.id,
        text: origem.name
      }));
    },
    funisOptions() {
      if (!this.crmConfig?.funis) return [];
      return this.crmConfig.funis.map((funil) => ({
        value: funil.id,
        text: funil.name
      }));
    },
    tiposLeadOptions() {
      if (!this.crmConfig?.tiposLead) return [];
      return this.crmConfig.tiposLead.map((tipo) => ({
        value: tipo.id,
        text: tipo.name
      }));
    },
    usuariosOptions() {
      if (!this.crmConfig?.usuarios) return [];
      return this.crmConfig.usuarios.map((usuario) => ({
        value: usuario.id,
        text: usuario.name
      }));
    },
    filteredStageOptions() {
      const search = this.normalizeText(this.stageSearch);

      if (!search) return this.updatableStageOptions;

      return this.updatableStageOptions.filter((stage) =>
        this.normalizeText(stage.text).includes(search)
      );
    },
    updatableStageOptions() {
      return this.estagiosOptions.filter(
        (stage) => !["won", "lost"].includes(stage.code)
      );
    },
    allStagesSelected() {
      return (
        this.updatableStageOptions.length > 0 &&
        this.updatableStageOptions.every((stage) =>
          this.formData.updatable_lead_pipeline_stage_ids.includes(stage.value)
        )
      );
    },
    someStagesSelected() {
      const selectedCount =
        this.formData.updatable_lead_pipeline_stage_ids.length;
      return (
        selectedCount > 0 && selectedCount < this.updatableStageOptions.length
      );
    },
    selectedStagesLabel() {
      const selectedIds = this.formData.updatable_lead_pipeline_stage_ids;

      if (!selectedIds.length || this.allStagesSelected) {
        return "Todos os estágios";
      }

      if (selectedIds.length === 1) {
        return (
          this.estagiosOptions.find((stage) => stage.value === selectedIds[0])
            ?.text || "1 estágio selecionado"
        );
      }

      return `${selectedIds.length} estágios selecionados`;
    }
  },
  methods: {
    normalizeText(value) {
      return (value || "")
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    onLeadBehaviorChange() {
      if (this.formData.always_create_new_lead) {
        this.formData.updatable_lead_pipeline_stage_ids = [];
      }

      this.updateJson();
    },
    toggleAllStages(event) {
      this.formData.updatable_lead_pipeline_stage_ids = event.target.checked
        ? this.updatableStageOptions.map((stage) => stage.value)
        : [];
      this.updateJson();
    },
    onFunilChange(load = false) {
      if (!load) {
        // Limpa o estágio selecionado quando o funil muda
        this.formData.lead_pipeline_stage_id = null;
        this.formData.updatable_lead_pipeline_stage_ids = [];
      }

      // Atualiza as opções de estágios baseado no funil selecionado
      if (this.formData.lead_pipeline_id && this.crmConfig?.funis) {
        const funilSelecionado = this.crmConfig.funis.find(
          (funil) => funil.id === this.formData.lead_pipeline_id
        );

        if (funilSelecionado?.stages) {
          this.estagiosOptions = funilSelecionado.stages.map((stage) => ({
            value: stage.id,
            text: stage.name,
            code: stage.code
          }));

          const updatableStageIds = this.updatableStageOptions.map(
            (stage) => stage.value
          );
          this.formData.updatable_lead_pipeline_stage_ids =
            this.formData.updatable_lead_pipeline_stage_ids.filter((stageId) =>
              updatableStageIds.includes(stageId)
            );
        } else {
          this.estagiosOptions = [];
        }
      } else {
        this.estagiosOptions = [];
      }

      this.updateJson();
    },
    updateJson() {
      this.json["CRM"] = JSON.stringify({
        ...this.salvarDados()
      });
    },
    salvarDados() {
      return {
        title: this.formData.title || "Lead de campanha",
        description: this.formData.description || "Lead de campanha",
        lead_pipeline_id: this.formData.lead_pipeline_id,
        lead_pipeline_stage_id: this.formData.lead_pipeline_stage_id,
        lead_source_id: this.formData.lead_source_id,
        lead_type_id: this.formData.lead_type_id,
        user_id: this.formData.user_id,
        always_create_new_lead: this.formData.always_create_new_lead,
        updatable_lead_pipeline_stage_ids:
          this.formData.updatable_lead_pipeline_stage_ids,
        products: this.formData.product_id
          ? {
              product_0: {
                product_id: this.formData.product_id,
                name:
                  this.produtosOptions.find(
                    (p) => p.value === this.formData.product_id
                  )?.text || "Produto",
                quantity: 1,
                price: 0
              }
            }
          : {},
        person: {
          name: "{cliente_nome}",
          email: [
            {
              value: "{cliente_email}",
              label: "work"
            }
          ],
          contact_numbers: [
            {
              value: "{destinatario}",
              label: "work"
            }
          ]
        },
        entity_type: "leads"
      };
    },
    async preencherForm(objJson) {
      this.formData = {
        title: objJson.title || null,
        description: objJson.description || null,
        lead_pipeline_id: objJson.lead_pipeline_id || null,
        lead_pipeline_stage_id: objJson.lead_pipeline_stage_id || null,
        lead_source_id: objJson.lead_source_id || null,
        lead_type_id: objJson.lead_type_id || null,
        user_id: objJson.user_id || null,
        product_id: objJson.products?.product_0?.product_id || null,
        always_create_new_lead: objJson.always_create_new_lead ?? false,
        updatable_lead_pipeline_stage_ids: Array.isArray(
          objJson.updatable_lead_pipeline_stage_ids
        )
          ? objJson.updatable_lead_pipeline_stage_ids
          : []
      };

      if (this.formData.lead_pipeline_id) {
        this.onFunilChange(true);
      } else {
        this.updateJson();
      }
    },
    handleFocus(event) {
      this.$emit("focused", event.target.id);
    },
    async pegarParametrosChatbot() {
      try {
        const res = await ApiService.get(
          "/conta/crm/parametros-chatbot",
          "",
          true,
          {
            baseURL: process.env.VUE_APP_COMUNICANTE_API_PAINEL
          }
        );
        this.crmConfig = res.data.data;
        this.parametrosCarregados = true;

        if (this.formData.lead_pipeline_id) {
          this.onFunilChange(true);
        }
      } catch (error) {
        this.crmConfig = { funis: [] };
      } finally {
        this.loading = false;
      }
    }
  },
  created() {
    if (this.json["CRM"]) {
      const objJson = JSON.parse(this.json["CRM"]);
      this.preencherForm(objJson);
    } else {
      this.updateJson();
    }
    this.pegarParametrosChatbot();
  },
  props: {
    json: {
      required: true
    }
  }
};
</script>

<style scoped>
.crm-options {
  margin: 0 auto;
  color: #2f3446;
}
.scroll-container {
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 15px;
}

.behavior-section,
.stage-filter-section {
  display: flex;
  gap: 24px;
  padding: 20px;
  border: 1px solid #e2e5ec;
  border-radius: 8px;
  background: #fff;
}

.behavior-copy,
.stage-filter-copy {
  flex: 1 1 38%;
  min-width: 250px;
}

.behavior-copy h6,
.stage-filter-copy h6 {
  margin-bottom: 8px;
  color: #2f3446;
  font-size: 15px;
  font-weight: 600;
}

.behavior-copy p,
.stage-filter-copy p,
.stage-filter-copy small {
  display: block;
  margin: 0;
  color: #6f7485;
  font-size: 13px;
  line-height: 1.5;
}

.stage-filter-copy span {
  font-weight: 400;
}

.stage-filter-copy small {
  margin-top: 4px;
}

.behavior-options {
  display: grid;
  flex: 1 1 62%;
  grid-template-columns: repeat(2, minmax(210px, 1fr));
  gap: 10px;
}

.behavior-option {
  display: grid;
  grid-template-columns: 18px 32px 1fr;
  gap: 12px;
  align-items: center;
  min-height: 86px;
  margin: 0;
  padding: 16px;
  border: 1px solid #dde1e9;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.behavior-option:hover {
  border-color: #8ca9ff;
}

.behavior-option.selected {
  border-color: #356cf6;
  background: #f7f9ff;
  box-shadow: 0 0 0 1px rgba(53, 108, 246, 0.05);
}

.behavior-option strong,
.behavior-option small {
  display: block;
}

.behavior-option strong {
  margin-bottom: 7px;
  color: #353a4e;
  font-size: 13px;
  font-weight: 600;
}

.behavior-option small {
  color: #777d8e;
  font-size: 12px;
  line-height: 1.4;
}

.behavior-option.selected strong,
.behavior-option.selected .behavior-icon {
  color: #356cf6;
}

.radio-indicator {
  width: 18px;
  height: 18px;
  border: 1px solid #bbc1cc;
  border-radius: 50%;
  background: #fff;
}

.behavior-option.selected .radio-indicator {
  border: 5px solid #356cf6;
}

.behavior-icon {
  color: #596075;
  font-size: 24px;
  text-align: center;
}

.stage-filter-section {
  align-items: center;
}

.stage-dropdown {
  flex: 1 1 38%;
  min-width: 300px;
}

.stage-dropdown ::v-deep .dropdown-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 42px;
  padding: 0 14px;
  color: #46506a;
  border-color: #356cf6;
  background: #fff;
  text-align: left;
}

.stage-dropdown ::v-deep .dropdown-toggle::after {
  display: none;
}

.stage-dropdown ::v-deep .dropdown-toggle .fa-chevron-down {
  color: #356cf6;
  font-size: 11px;
}

.stage-dropdown ::v-deep .dropdown-menu {
  width: 100%;
  min-width: 300px;
  max-height: 310px;
  padding: 0;
  overflow: hidden;
  border: 1px solid #e1e4eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(39, 45, 66, 0.14);
}

.stage-dropdown ::v-deep .b-dropdown-form {
  padding: 12px;
}

.stage-search {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e2e5ec;
}

.stage-search i {
  color: #7f8698;
  font-size: 14px;
}

.stage-search ::v-deep .form-control {
  height: 38px;
  padding-left: 10px;
  border: 0;
  box-shadow: none;
  font-size: 13px;
}

.stage-list {
  max-height: 230px;
  padding-top: 8px;
  overflow-y: auto;
}

.stage-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 34px;
  margin: 0;
  padding: 5px 2px;
  color: #4d5367;
  cursor: pointer;
  font-size: 13px;
  font-weight: 400;
}

.stage-checkbox.select-all {
  margin-bottom: 4px;
  padding-bottom: 9px;
  border-bottom: 1px solid #eef0f4;
}

.stage-checkbox input {
  width: 16px;
  height: 16px;
  accent-color: #356cf6;
}

.stage-empty {
  margin: 10px 2px;
  color: #858b9b;
  font-size: 12px;
}

@media (max-width: 991.98px) {
  .behavior-section,
  .stage-filter-section {
    flex-direction: column;
  }

  .behavior-copy,
  .stage-filter-copy,
  .stage-dropdown {
    width: 100%;
    min-width: 0;
  }
}

@media (max-width: 767.98px) {
  .behavior-options {
    width: 100%;
    grid-template-columns: 1fr;
  }
}
</style>
